import { useRef, useState } from "react";
import { playNow, kickAudio } from "@/audio/piano";
import { useMicNotes } from "@/audio/useMicNotes";
import { judgeInterval, intervalName } from "@/grading/grade";

/**
 * IntervalSingBack — the other half of ear training.
 *
 * `IntervalEarTrainer` asks the student to NAME what they hear. Naming can be
 * done by elimination; singing cannot. A student who can sing a perfect 5th
 * back has internalised the distance, which is the actual skill the course is
 * trying to build.
 *
 * The scoring rule is the whole point, and it is deliberate: we judge the
 * DISTANCE between the two notes sung, never which notes they were. A tenor
 * who answers an octave below the piano and a soprano who answers an octave
 * above have both sung a perfect 5th, and any app that marks them wrong is
 * teaching absolute pitch by accident — the opposite of relative-pitch
 * training, and impossible for most people to satisfy.
 *
 * The microphone means this works everywhere: no MIDI keyboard, no Web MIDI
 * (which Safari and iPad do not have), no instrument at all. A voice is enough.
 */

type IntervalDef = { name: string; fullName: string; semitones: number };

const ALL_INTERVALS: IntervalDef[] = [
  { name: "m2", fullName: "Minor 2nd", semitones: 1 },
  { name: "M2", fullName: "Major 2nd", semitones: 2 },
  { name: "m3", fullName: "Minor 3rd", semitones: 3 },
  { name: "M3", fullName: "Major 3rd", semitones: 4 },
  { name: "P4", fullName: "Perfect 4th", semitones: 5 },
  { name: "Tritone", fullName: "Tritone", semitones: 6 },
  { name: "P5", fullName: "Perfect 5th", semitones: 7 },
  { name: "m6", fullName: "Minor 6th", semitones: 8 },
  { name: "M6", fullName: "Major 6th", semitones: 9 },
  { name: "m7", fullName: "Minor 7th", semitones: 10 },
  { name: "M7", fullName: "Major 7th", semitones: 11 },
  { name: "Octave", fullName: "Octave", semitones: 12 },
];

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const midiToPitch = (m: number) => NOTE_NAMES[m % 12] + (Math.floor(m / 12) - 1);
/** Plain note name for the tuner — no octave numbers on anything a student reads. */
const plainName = (m: number) => NOTE_NAMES[((Math.round(m) % 12) + 12) % 12];

type Verdict = { verdict: "correct" | "compound" | "wrong"; sungSemitones: number } | null;

export function IntervalSingBack({ vocabulary }: { vocabulary?: string[] }) {
  const intervals = vocabulary
    ? ALL_INTERVALS.filter((i) => vocabulary.includes(i.name))
    : ALL_INTERVALS;

  const [current, setCurrent] = useState<IntervalDef | null>(null);
  const [verdict, setVerdict] = useState<Verdict>(null);
  const [heard, setHeard] = useState<number[]>([]);
  const [score, setScore] = useState({ right: 0, wrong: 0 });

  const rootRef = useRef(60);
  // The take lives in a ref: notes arrive from the audio thread and must not
  // wait on a render, and React StrictMode would double-invoke a set-state
  // updater that did the judging.
  const takeRef = useRef<number[]>([]);
  const currentRef = useRef<IntervalDef | null>(null);
  const armedRef = useRef(false);

  const mic = useMicNotes((n) => {
    if (!armedRef.current) return;
    const take = takeRef.current;
    // Ignore an immediate repeat of the same note — a wobbling held note is
    // one note, not the second half of an answer.
    if (take.length && take[take.length - 1] === n.midi) return;
    take.push(n.midi);
    setHeard([...take]);
    if (take.length < 2) return;

    armedRef.current = false;
    const target = currentRef.current;
    if (!target) return;
    const j = judgeInterval(take[0], take[1], target.semitones);
    setVerdict(j);
    setScore((s) => ({
      right: s.right + (j.verdict === "wrong" ? 0 : 1),
      wrong: s.wrong + (j.verdict === "wrong" ? 1 : 0),
    }));
  });

  const playInterval = (iv: IntervalDef) => {
    const root = rootRef.current;
    void playNow(midiToPitch(root), "2n", 0.85);
    setTimeout(() => void playNow(midiToPitch(root + iv.semitones), "2n", 0.85), 700);
  };

  const newQuestion = () => {
    kickAudio();
    const pick = intervals[Math.floor(Math.random() * intervals.length)];
    rootRef.current = 55 + Math.floor(Math.random() * 8); // G3–D4, easy to hear
    currentRef.current = pick;
    takeRef.current = [];
    armedRef.current = false;
    setCurrent(pick);
    setVerdict(null);
    setHeard([]);
    playInterval(pick);
  };

  const listen = async () => {
    takeRef.current = [];
    setHeard([]);
    setVerdict(null);
    if (mic.status !== "listening") await mic.start();
    armedRef.current = true;
  };

  const total = score.right + score.wrong;
  const pct = total ? Math.round((score.right / total) * 100) : 0;

  return (
    <div className="my-8 rounded-2xl border border-nsm-border bg-nsm-card/40 p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-nsm-dim">
            Sing it back
          </div>
          <p className="mt-1 max-w-lg text-[13px] leading-snug text-nsm-dim">
            Start anywhere that sits comfortably in your voice. You are being
            marked on the <em>distance</em> between your two notes, not on
            matching the piano&rsquo;s pitch.
          </p>
        </div>
        {total > 0 && (
          <div className="text-right">
            <div className="font-display text-2xl font-bold text-nsm-ink">{pct}%</div>
            <div className="text-[11px] text-nsm-dim">
              {score.right} of {total}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={newQuestion}
          className="rounded-lg bg-nsm-ink px-4 py-2 text-[13px] font-semibold text-nsm-bg transition hover:opacity-90"
        >
          {current ? "New interval" : "Play an interval"}
        </button>
        {current && (
          <button
            onClick={() => playInterval(current)}
            className="rounded-lg border border-nsm-border px-4 py-2 text-[13px] font-semibold text-nsm-ink transition hover:bg-nsm-surface"
          >
            Hear it again
          </button>
        )}
        {current && (
          <button
            onClick={listen}
            disabled={mic.status === "starting"}
            className="rounded-lg border border-emerald-500/40 bg-emerald-500/15 px-4 py-2 text-[13px] font-semibold text-emerald-200 transition hover:bg-emerald-500/25 disabled:opacity-50"
          >
            {mic.status === "starting"
              ? "Opening the mic…"
              : armedRef.current
                ? "Listening…"
                : "🎤 Sing it back"}
          </button>
        )}
      </div>

      {/* Live tuner — proof to the student that the mic is hearing them. */}
      {mic.status === "listening" && (
        <div className="mt-3 flex items-center gap-3 text-[12px] text-nsm-dim">
          <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
          {mic.liveMidi != null ? (
            <span>
              Hearing <strong className="text-nsm-ink">{plainName(mic.liveMidi)}</strong>
            </span>
          ) : (
            <span>Listening — sing the two notes, one after the other.</span>
          )}
          {heard.length > 0 && (
            <span>
              · caught {heard.map((m) => plainName(m)).join(" → ")}
            </span>
          )}
        </div>
      )}

      {mic.status === "error" && (
        <p className="mt-3 text-[12px] text-rose-300">
          {mic.error} — check that the browser is allowed to use your microphone,
          then press <strong>Sing it back</strong> again.
        </p>
      )}

      {verdict && current && (
        <div
          className={`mt-4 rounded-xl border px-4 py-3 text-[13px] leading-relaxed ${
            verdict.verdict === "correct"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
              : verdict.verdict === "compound"
                ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
                : "border-rose-500/40 bg-rose-500/10 text-rose-100"
          }`}
        >
          {verdict.verdict === "correct" && (
            <>
              <strong>That&rsquo;s the {current.fullName.toLowerCase()}.</strong>{" "}
              You sang it from {plainName(heard[0])} — a different starting note
              from the piano, and still exactly right. That is relative pitch
              working.
            </>
          )}
          {verdict.verdict === "compound" && (
            <>
              <strong>Right interval, wrong register.</strong> You sang the{" "}
              {current.fullName.toLowerCase()} an octave out (
              {verdict.sungSemitones} semitones). Musically that is a compound{" "}
              {current.fullName.toLowerCase()} — try to keep both notes inside
              one octave.
            </>
          )}
          {verdict.verdict === "wrong" && (
            <>
              <strong>Not quite.</strong> You sang{" "}
              {heard.length >= 2 ? `${plainName(heard[0])} to ${plainName(heard[1])} — ` : ""}
              a {intervalName(verdict.sungSemitones)}. The target was a{" "}
              {current.fullName.toLowerCase()}. Hear it again, hum the first note
              until it settles, then move.
            </>
          )}
        </div>
      )}
    </div>
  );
}
